import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { StringIgnoreCaseComparator } from 'src/app/shared/utils/comparators';
import { AttributeDefinitionWrite } from '../models/attributes/attributeDefinitionWrite';
import { BaseService } from './base.service';
import _ from 'lodash';
import { AttributeDefinitionUsageReport } from '../models/attributes/attributeDefinitionUsageReport ';
import { ISharedAttributeService } from 'src/shared-app-portal/interfaces/i-shared-attribute-service';

@Injectable()
export class AttributeService extends BaseService implements ISharedAttributeService {
  private basePath = 'attributes';

  protected _attributes: BehaviorSubject<AttributeDefinition[]> = new BehaviorSubject<
    AttributeDefinition[]
  >(null);
  private _attributesLoaded = false;
  private _attributesInFlight: Promise<AttributeDefinition[]> | null = null;

  protected _attributesForAdminPage: BehaviorSubject<AttributeDefinition[]> = new BehaviorSubject<
    AttributeDefinition[]
  >(null);
  public get attributes(): Observable<AttributeDefinition[]> {
    return this._attributes.pipe(map((x) => x?.filter((a) => !a.isSubField)));
  }

  public get attributesForAdminPage(): Observable<AttributeDefinition[]> {
    return this._attributesForAdminPage.asObservable();
  }
  public attributeDeleted = new Subject<any>();
  public attributeWorkspaceUpdated = new Subject<any>();
  public unfilterableAttribute = new Subject<AttributeDefinition>();

  constructor() {
    super();
  }

  getAttributeDefinitions = (): Promise<AttributeDefinition[]> => {
    if (this._attributesLoaded) {
      return Promise.resolve(this._attributes.getValue());
    }
    if (this._attributesInFlight) {
      return this._attributesInFlight;
    }
    this._attributesInFlight = lastValueFrom(
      this.get(`${this.basePath}?filterByActiveWorkspace=true`)
    )
      .then((data) => data.map((attrType) => AttributeDefinition.parse(attrType)))
      .then((attributeDefinition: AttributeDefinition[]) =>
        attributeDefinition.filter((x) => !x.isArchived).sort(StringIgnoreCaseComparator('name'))
      )
      .then((attributeDefinitions: AttributeDefinition[]) => {
        this._attributes.next(attributeDefinitions);
        this._attributesLoaded = true;
        return attributeDefinitions;
      })
      .finally(() => {
        this._attributesInFlight = null;
      });
    return this._attributesInFlight;
  };

  getAttributeDefinitionsForAdminPage(workspaceId: string = null) {
    let path = this.basePath;
    if (workspaceId) {
      path = path.concat(`?workspaceId=${workspaceId}`);
    }
    let obs = this.get(path).pipe(
      map((data) => data.map((attrType) => AttributeDefinition.parse(attrType)))
    );

    obs.subscribe((data) => {
      this._attributesForAdminPage.next(data.sort(StringIgnoreCaseComparator('name')));
    });
  }

  getSubFiledDefinitions(): Observable<AttributeDefinition[]> {
    let path = this.basePath.concat('/sub-field-definitions');
    let obs = this.get(path).pipe(
      map((data) =>
        data
          .filter((x) => x.isSubField)
          .sort(StringIgnoreCaseComparator('name'))
          .map((attrType) => AttributeDefinition.parse(attrType))
      )
    );
    return obs;
  }

  getSeatAttributeDefinitions(): Observable<AttributeDefinition[]> {
    return this.getAttributeDefinitionsByEntityType(AttributeEntityType.Seat);
  }

  getSurveyAttributeDefinitions(): Observable<AttributeDefinition[]> {
    return this.getAttributeDefinitionsByEntityType(AttributeEntityType.Survey);
  }

  getEvaluationAttributeDefinitions(): Observable<AttributeDefinition[]> {
    return this.getAttributeDefinitionsByEntityType(AttributeEntityType.Evaluation);
  }

  getAttributeDefinitionsByEntityType(
    type: AttributeEntityType,
    workspaceId: string = null
  ): Observable<AttributeDefinition[]> {
    if (!this._attributesForAdminPage.getValue() || workspaceId) {
      this.getAttributeDefinitionsForAdminPage(workspaceId);
    }
    return this._attributesForAdminPage.pipe(
      map((attributeDefinitions) =>
        attributeDefinitions
          ? attributeDefinitions.filter((definition) => definition.entityType === type)
          : null
      )
    );
  }

  updateAttributes(attributes) {
    let attributeDefinitions = this._attributesForAdminPage.value;
    _.each(attributes, function (attribute) {
      var attributeDefinitionToAddTo = _.find(
        attributeDefinitions,
        function (attributeDefinition: AttributeDefinition) {
          return attributeDefinition.id === attribute.attributeDefinitionId;
        }
      );

      attributeDefinitionToAddTo.valueList.push(attribute.attributeDefinitionValueListItem);
    });
    this._attributesForAdminPage.next(attributeDefinitions);
  }

  create(attribute: AttributeDefinitionWrite): Observable<AttributeDefinition> {
    let body = JSON.stringify(attribute);
    return this.post(this.basePath, body).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((newAttribute) => {
        this.addAttribute(newAttribute);
        return newAttribute;
      })
    );
  }

  update(attribute: AttributeDefinition): Observable<AttributeDefinition> {
    let path = `${this.basePath}/${attribute.id}`;
    let body = JSON.stringify(attribute);
    return this.put(path, body).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        if (!updatedAttribute) return null;
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  updateFeatured(attribute: AttributeDefinition): Observable<AttributeDefinition> {
    return this.patch(this.basePath + '/' + attribute.id + '/featured', {
      value: attribute.featured
    }).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  updateHasVisualization(attribute: AttributeDefinition): Observable<AttributeDefinition> {
    return this.patch(this.basePath + '/' + attribute.id + '/showChart', {
      value: attribute.hasVisualization
    }).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  updateShowOnCreate(attribute: AttributeDefinition): Observable<AttributeDefinition> {
    return this.patch(this.basePath + '/' + attribute.id + '/show-on-create', {
      value: attribute.showOnCreate
    }).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  updateHasOther(attribute: AttributeDefinition): Observable<AttributeDefinition> {
    return this.patch(this.basePath + '/' + attribute.id + '/has-other', {
      value: attribute.hasOther
    }).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  updateFilterable(attribute: AttributeDefinition): Observable<AttributeDefinition> {
    return this.patch(this.basePath + '/' + attribute.id + '/filterable', {
      value: attribute.filterable
    }).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  updateIncludeInRatingCalculation(
    attribute: AttributeDefinition
  ): Observable<AttributeDefinition> {
    return this.patch(this.basePath + '/' + attribute.id + '/includeInRatingCalculation', {
      value: attribute.includeInRatingCalculation
    }).pipe(
      map((response) => AttributeDefinition.parse(response)),
      map((updatedAttribute) => {
        this.publishChangeToAttribute(updatedAttribute);
        return updatedAttribute;
      })
    );
  }

  getUsageReport = (attribute: AttributeDefinition) =>
    lastValueFrom(
      this.get<AttributeDefinitionUsageReport>(`${this.basePath}/${attribute.id}/usage`).pipe(
        map((result) => new AttributeDefinitionUsageReport(result))
      )
    );

  deleteAttribute(attribute: AttributeDefinition) {
    var path = `${this.basePath}/${attribute.id}`;
    this.delete(path).subscribe(() => {
      let attributesForAdminPage = this._attributesForAdminPage.value;
      attributesForAdminPage.splice(
        attributesForAdminPage.findIndex((a) => a.id === attribute.id),
        1
      );
      attributesForAdminPage.sort(StringIgnoreCaseComparator('name'));
      this._attributesForAdminPage.next(attributesForAdminPage);

      let attributes = this._attributes.value;
      attributes.splice(
        attributes.findIndex((a) => a.id === attribute.id),
        1
      );
      attributes.sort(StringIgnoreCaseComparator('name'));
      this._attributes.next(attributes);
      this.attributeDeleted.next(attribute);
    });
  }

  private addAttribute(attributeDefinition: AttributeDefinition) {
    let attributesForAdminPage = this._attributesForAdminPage.value;
    attributesForAdminPage.push(attributeDefinition);
    attributesForAdminPage.sort(StringIgnoreCaseComparator('name'));
    this._attributesForAdminPage.next(attributesForAdminPage);

    let attributes = this._attributes.value;
    attributes.push(attributeDefinition);
    attributes.sort(StringIgnoreCaseComparator('name'));
    this._attributes.next(attributes);
  }

  private publishChangeToAttribute(attrType: AttributeDefinition) {
    let attributesForAdminPage = this._attributesForAdminPage.value;
    let attributes = this._attributes.value;
    this.replaceAttribute(attributesForAdminPage, attrType);
    this.replaceAttribute(attributes, attrType);
    if (!attrType.filterable) {
      this.unfilterableAttribute.next(attrType);
    }
    this._attributesForAdminPage.next(attributesForAdminPage);
    this._attributes.next(attributes);
  }

  private replaceAttribute(attributes: AttributeDefinition[], attrType: AttributeDefinition): void {
    let attributeDefinitionToUpdate = _.find(
      attributes,
      function (oldAttributeDefinition: AttributeDefinition) {
        return oldAttributeDefinition.id === attrType.id;
      }
    );
    let updateIndex = attributes.indexOf(attributeDefinitionToUpdate);
    if (updateIndex !== -1) {
      attributes[updateIndex] = attrType;
    }
  }

  decryptAttribute(attributeDefinitionId: string, encryptedValue: string): Observable<any> {
    let path = `attributes/${attributeDefinitionId}/decrypt/${encryptedValue}`;
    return this.put(path);
  }
}
